import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input, Select, SearchInput } from "../components/ui/Fields";
import { Avatar } from "../components/ui/Avatar";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";
import { useOffsetList, fmtDuration } from "../hooks/useOffsetList";
import type { HostRow, AgencyUserLookup } from "../api/types";

const statusTone = (s: string) => (s === "active" ? "success" : s === "suspended" ? "warn" : "danger");
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Round 54 — Hosts. A host is a role on an existing user (no duplicate
 * accounts); each gets its own unique 7-digit Host ID. Speaking time,
 * charm and diamonds shown here are computed by the server from verified
 * room/voice sessions and the diamond ledger for the chosen date range.
 */
export function HostsPage() {
  const [q, setQ] = useState("");
  const [agency, setAgency] = useState("");
  const [country, setCountry] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [online, setOnline] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { rows, isLoading, error, page, setPage, hasNextPage, reload, pageSize } = useOffsetList<HostRow>("/admin/hosts", {
    q: q.trim(), agency: agency.trim(), country: country.trim(), status, type, online, from, to,
  });
  const [adding, setAdding] = useState(false);
  const [managing, setManaging] = useState<HostRow | null>(null);

  const columns: Column<HostRow>[] = [
    { header: "Host ID", render: (r) => <span className="font-mono text-xs">{r.host_id}</span> },
    { header: "User ID", render: (r) => r.public_user_id },
    { header: "Name", render: (r) => <span className="font-medium">{r.username}</span> },
    { header: "Country", render: (r) => r.country ?? "—" },
    { header: "Agency", render: (r) => (r.agency_id ? <span>{r.agency_name} <span className="text-xs text-ink-faint">{r.agency_id}</span></span> : <Badge tone="gold">Independent</Badge>) },
    { header: "Status", render: (r) => <span className="flex items-center gap-1"><Badge tone={statusTone(r.status)}>{r.status}</Badge>{r.online && <Badge tone="success">online</Badge>}</span> },
    { header: "Created", render: (r) => new Date(r.created_at).toLocaleDateString() },
    { header: "Last Active", render: (r) => (r.last_active ? new Date(r.last_active).toLocaleString() : "—") },
    { header: "Speaking", render: (r) => fmtDuration(r.speaking_seconds) },
    { header: "Charm", render: (r) => r.charm.toLocaleString() },
    { header: "Diamonds", render: (r) => r.diamonds.toLocaleString() },
    { header: "Rooms", render: (r) => r.rooms },
    { header: "", render: (r) => <Button size="sm" variant="secondary" onClick={() => setManaging(r)}>Manage</Button> },
  ];

  return (
    <AppShell title="Hosts">
      <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-8">
        <div className="col-span-2"><SearchInput value={q} onChange={setQ} placeholder="User ID, Host ID, name, Agency ID/name..." /></div>
        <Input placeholder="Agency ID" value={agency} onChange={(e) => setAgency(e.target.value)} />
        <Input placeholder="Country" value={country} onChange={(e) => setCountry(e.target.value)} />
        <Select value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Any status</option><option value="active">Active</option><option value="suspended">Suspended</option><option value="disabled">Disabled</option><option value="removed">Removed</option></Select>
        <Select value={type} onChange={(e) => setType(e.target.value)}><option value="">Agency + Independent</option><option value="agency">Agency</option><option value="independent">Independent</option></Select>
        <Select value={online} onChange={(e) => setOnline(e.target.value)}><option value="">Online + Offline</option><option value="true">Online</option><option value="false">Offline</option></Select>
        <Button onClick={() => setAdding(true)}>Add Host</Button>
      </div>
      <div className="mb-4 flex items-end gap-2 text-xs text-ink-muted">
        <div className="w-40"><Input type="date" label="Stats from" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div className="w-40"><Input type="date" label="Stats to" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <span className="pb-2">Speaking time, charm and diamonds are summed over this range (blank = all time).</span>
      </div>
      <DataTable columns={columns} rows={rows} keyField={(r) => r.host_id} isLoading={isLoading} error={error} onRetry={reload}
        emptyTitle="No hosts match" pagination={{ page, pageSize, hasNextPage, onPageChange: setPage }} />
      {adding && <AddHostModal onClose={() => setAdding(false)} onDone={() => { setAdding(false); reload(); }} />}
      {managing && <ManageHostModal host={managing} onClose={() => setManaging(null)} onChanged={reload} />}
    </AppShell>
  );
}

function AddHostModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { showToast } = useToast();
  const [uid, setUid] = useState("");
  const [user, setUser] = useState<AgencyUserLookup | null>(null);
  const [type, setType] = useState<"agency" | "independent">("independent");
  const [agencyId, setAgencyId] = useState("");
  const [busy, setBusy] = useState(false);
  async function find() {
    setUser(null);
    try { setUser(await apiRequest<AgencyUserLookup>("/admin/agencies/lookup-user", { params: { user_id: uid.trim() } })); }
    catch (e) { showToast(e instanceof ApiError ? e.message : "Lookup failed", "error"); }
  }
  async function add() {
    setBusy(true);
    try {
      const r = await apiRequest<{ hostId?: string; host_id?: string }>("/admin/hosts", { method: "POST", body: { userId: uid.trim(), type, agencyId: type === "agency" ? agencyId.trim() : undefined } });
      showToast(`Host activated (Host ID ${r.hostId ?? r.host_id ?? "?"})`, "success");
      onDone();
    } catch (e) { showToast(e instanceof ApiError ? e.message : "Could not add host", "error"); }
    finally { setBusy(false); }
  }
  return (
    <Modal isOpen onClose={onClose} title="Add Host" footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button>
      <Button onClick={add} isLoading={busy} disabled={!user || (type === "agency" && !/^\d{7}$/.test(agencyId.trim()))}>Activate host</Button></>}>
      <div className="space-y-4">
        <div className="flex items-end gap-2"><Input label="User ID" value={uid} onChange={(e) => setUid(e.target.value)} />
          <Button variant="secondary" onClick={find} disabled={!/^\d+$/.test(uid.trim())}>Find</Button></div>
        {user && <div className="flex items-center gap-3 rounded-lg border border-surface-border p-3">
          <Avatar src={user.avatar_url} className="h-10 w-10 rounded-full object-cover" />
          <div className="text-sm"><p className="font-medium">{user.username}</p><p className="text-xs text-ink-faint">ID {user.user_id} · {user.country ?? "—"}
            {user.roles?.host ? ` · already host ${user.roles.host.hostId} (${user.roles.host.status})` : ""}{user.roles?.agency ? ` · in agency ${user.roles.agency.name} (${user.roles.agency.role})` : ""}</p></div></div>}
        <Select label="Host type" value={type} onChange={(e) => setType(e.target.value as "agency" | "independent")}>
          <option value="independent">Independent Host</option><option value="agency">Agency Host</option></Select>
        {type === "agency" && <Input label="Agency ID (7 digits)" value={agencyId} onChange={(e) => setAgencyId(e.target.value)} maxLength={7} />}
      </div>
    </Modal>
  );
}

interface Detail { stats: Record<string, number>; attendance: Array<Record<string, unknown>> }

function ManageHostModal({ host, onClose, onChanged }: { host: HostRow; onClose: () => void; onChanged: () => void }) {
  const { showToast } = useToast();
  const [reason, setReason] = useState("");
  const [target, setTarget] = useState("");
  const [from, setFrom] = useState(() => new Date(Date.now() - 6 * 864e5).toISOString().slice(0, 10));
  const [to, setTo] = useState(today());
  const [detail, setDetail] = useState<Detail | null>(null);
  const [busy, setBusy] = useState(false);

  async function post(path: string, body: Record<string, unknown>, ok: string) {
    setBusy(true);
    try { await apiRequest(path, { method: "POST", body: { userId: String(host.public_user_id), reason, ...body } }); showToast(ok, "success"); onChanged(); }
    catch (e) { showToast(e instanceof ApiError ? e.message : "Action failed", "error"); }
    finally { setBusy(false); }
  }
  async function load() {
    try { setDetail(await apiRequest<Detail>("/admin/hosts/detail", { params: { user_id: host.public_user_id, from, to } })); }
    catch (e) { showToast(e instanceof ApiError ? e.message : "Could not load stats", "error"); }
  }
  return (
    <Modal isOpen onClose={onClose} title={`${host.username} · Host ${host.host_id}`} footer={<Button variant="ghost" onClick={onClose}>Close</Button>}>
      <div className="space-y-5">
        <p className="text-sm text-ink-muted">{host.agency_id ? `Agency ${host.agency_name} (${host.agency_id})` : "Independent Host"} · <Badge tone={statusTone(host.status)}>{host.status}</Badge></p>
        <Input label="Reason (audit; required for transfer)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <section className="space-y-2"><h3 className="text-sm font-semibold">Status</h3>
          <div className="flex flex-wrap gap-2">{["active", "suspended", "disabled", "removed"].filter((s) => s !== host.status).map((s) => (
            <Button key={s} size="sm" variant={s === "active" ? "primary" : "danger"} isLoading={busy} onClick={() => post("/admin/hosts/status", { status: s }, `Host ${s}`)}>{s === "active" ? "Reactivate" : `Set ${s}`}</Button>))}</div>
          <p className="text-xs text-ink-faint">Suspended hosts keep their account and history but receive no host benefits.</p></section>
        <section className="space-y-2"><h3 className="text-sm font-semibold">Transfer</h3>
          <div className="flex items-end gap-2"><Input label="New Agency ID (blank = Independent)" value={target} onChange={(e) => setTarget(e.target.value)} maxLength={7} />
            <Button isLoading={busy} disabled={!reason.trim() || (target.trim() !== "" && !/^\d{7}$/.test(target.trim()))}
              onClick={() => post("/admin/hosts/transfer", { newAgencyId: target.trim() || null }, "Host transferred")}>Transfer</Button></div></section>
        <section className="space-y-2"><h3 className="text-sm font-semibold">Stats &amp; attendance</h3>
          <div className="flex items-end gap-2"><Input type="date" label="From" value={from} onChange={(e) => setFrom(e.target.value)} /><Input type="date" label="To" value={to} onChange={(e) => setTo(e.target.value)} />
            <Button variant="secondary" onClick={load}>Load</Button></div>
          {detail && <>
            <p className="text-xs text-ink-muted">Speaking {fmtDuration(Number(detail.stats.speakingSeconds))} · Charm {Number(detail.stats.charm).toLocaleString()} · Diamonds {Number(detail.stats.diamonds).toLocaleString()}</p>
            <ul className="max-h-48 divide-y divide-surface-border overflow-auto rounded-lg border border-surface-border text-xs">
              {detail.attendance.map((d, i) => <li key={i} className="px-3 py-2">{String(d.date)}: Active {fmtDuration(Number(d.activeSeconds))} / In Room {fmtDuration(Number(d.roomSeconds))} / Speaking {fmtDuration(Number(d.speakingSeconds))}</li>)}
            </ul></>}
        </section>
      </div>
    </Modal>
  );
}
